import { uniq, debounce, isEmpty } from 'lodash';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Container } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { checkArray, imageUrl, shortenandHoverString } from '../../../../Components/helper';
import { RenderHtmlComponent } from '../RenderHtmlComponent';
import * as action from '../store/ProductList.action';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import CommonImageComponent from '../../../../Components/Utility/CommonImage';
import Loader from '../../../../Components/Utility/Loader/Loader';
import noImage from '../../../../assets/images/no-image.png';
import { useProductEditNavigation } from '../../../../hooks/useProductEditNavigation';
import RenderProductInformationColouredCard from '../RangePlanTables/RenderProductInformationColouredCard';
import ColumnCalculation, { ColumnTotalsDisplay } from '../RangePlanTables/ColumnCalculation';
import { maxTickedScopes, sumColumnAcrossRows } from '../RangePlanTables/ColumnCalculation/selectors';
import { loadColumnScopes } from '../utils/columnTotalsStorage';
import { getCalculationColumnIds, getPagesForProducts, mergeCalculationColumns } from '../utils/calculation-refresh';
import { getCustomSectionDataService } from '../../../../services/ProductList.services';
import { logError } from '../../../../utils/logger';
import { useCostingAccess } from '../../../../Components/CustomHooks/useCostingAccess';
import { hideCostingColumns } from '../utils/costing-columns';

const COLUMN_TOTALS_HEIGHT_BY_COUNT = [0, 24, 44];

const dateTimeFormat = window?.Intl?.DateTimeFormat?.();
const timeZone = dateTimeFormat ? dateTimeFormat?.resolvedOptions()?.timeZone : undefined;

/** Computes the empty-state row colSpan to mirror the table header (4 fixed + colourway/size/length combo + dynamic section columns). */
const getCustomSectionColSpan = (customSectionDetails, allSectionColumns) => {
  const hasColourway = !!customSectionDetails?.colourway;
  const hasSizeLength = !!customSectionDetails?.sizeLength;
  let comboCols = 0;
  if (hasColourway && hasSizeLength) comboCols = 3;
  else if (hasColourway) comboCols = 1;
  else if (hasSizeLength) comboCols = 2;
  const sectionCols = Array.isArray(allSectionColumns) ? allSectionColumns.length : 0;
  return 4 + comboCols + sectionCols;
};

/**
 * Range Plan expand-view custom section table. Renders the section header, columns, and rows
 * for a single customSection, supporting colourway / size-length combinations, column totals,
 * inline cell edits, and pagination.
 */
export function ProductInfoCustomSections({
  section,
  handleSelection,
  filteredDataObj,
  handleCustomSectionSelection,
  colouredCardFaces = false,
  colouredFaces = false,
  selectedCardAttributes = [],
  handleCombinationSelection,
  selectedCombinations = [],
}) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { rangeplanId } = useParams();

  const ProductTestingData = useSelector((redState) => redState);
  const { ProductListData, Users } = ProductTestingData;
  const { customSectionData, allSectionColumns: storedSectionColumns } = ProductListData || {};
  const hasCostingAccess = useCostingAccess();
  const allSectionColumns = useMemo(() => hideCostingColumns(storedSectionColumns, hasCostingAccess), [storedSectionColumns, hasCostingAccess]);
  const maxScopes = useSelector((s) => maxTickedScopes(s.ProductListData?.columnTotals));
  const anyColumnTotalsActive = maxScopes > 0;
  const columnTotalsHeight = COLUMN_TOTALS_HEIGHT_BY_COUNT[maxScopes] ?? 64;
  const { UserDetails } = ProductTestingData;
  const { userInfo } = UserDetails || {};
  const { logInRights } = Users || {};
  const loggedInUserId = userInfo?.userId;
  const { customSectionDetails } = customSectionData || {};
  const { CheckActionPermission } = useComponent();
  const rangePlanAddEditAccess = CheckActionPermission(logInRights, 'rangePlan', 'default', 'addEdit');

  const [expandedRowIndex, setExpandedRowIndex] = useState([]);
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [localTableData, setLocalTableData] = useState([]);
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [localIsSelectAll, setLocalIsSelectAll] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const pendingHasMoreRef = useRef(true);
  const MIN_LOADER_VISIBLE_MS = 500;

  const [changedCells, setChangedCells] = useState(new Map());

  const manualNumberColumns = useMemo(() => {
    if (!Array.isArray(allSectionColumns)) return [];
    return allSectionColumns.filter((col) => col?.columnType === 'manual' && col?.type === 'Number');
  }, [allSectionColumns]);

  const clientSideTotals = useMemo(() => {
    const result = {};
    if (manualNumberColumns.length === 0) return result;
    const selectedIdSet = new Set(localSelectedIds);
    const selectedRows = localSelectedIds.length === 0
      ? []
      : localTableData.filter((row) => selectedIdSet.has(row?._id));
    for (const col of manualNumberColumns) {
      const colId = String(col._id);
      result[colId] = { selected: sumColumnAcrossRows(selectedRows, colId) };
    }
    return result;
  }, [localTableData, localSelectedIds, manualNumberColumns]);

  const currentSectionIdRef = useRef(null);
  const tableDataRef = useRef([]);
  const pendingSaveRef = useRef(null);
  const filtersRef = useRef(null);
  useEffect(() => {
    filtersRef.current = filteredDataObj || null;
  }, [filteredDataObj]);

  const lastFiltersJsonRef = useRef(null);
  const filterResyncPendingRef = useRef(false);

  const { goToProductEdit } = useProductEditNavigation();

  const formatNumberUpToTwoDecimals = (num) => {
    const rounded = Math.round(Number(num) * 100) / 100;
    return parseFloat(rounded.toFixed(2)).toString();
  };

  const stripTrailingDecimalZeros = (str) => str.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

  const isPurelyNumericString = (str) => /^-?\d+(\.\d*)?$/.test(str);

  const formatCellValue = (value) => {
    if (value === null || value === undefined) {
      return '--';
    }

    if (typeof value === 'number' && !Number.isNaN(value)) {
      return stripTrailingDecimalZeros(formatNumberUpToTwoDecimals(value));
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '--';
      if (!isPurelyNumericString(trimmed)) {
        return trimmed;
      }
      return stripTrailingDecimalZeros(trimmed);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '--';
      }
      if (value[0] && typeof value[0] === 'object' && value[0]?.name) {
        return value.map((item) => item?.name).join(', ');
      }
      return value.join(', ');
    }

    if (typeof value === 'object') {
      if (value?.name) {
        return value.name;
      }
      if (value?._id && value?.name) {
        return value.name;
      }
      return JSON.stringify(value);
    }

    return '--';
  };
  useEffect(() => {
    if (section?._id && allSectionColumns) {
      currentSectionIdRef.current = section._id;
      setPage(1);
      setHasMore(true);
      setLoadingMore(false);
      setLocalTableData([]);
      setLocalSelectedIds([]);
      setLocalIsSelectAll(false);
      setExpandedRowIndex([]);
      setIsExpandAll(false);
      setChangedCells(new Map());
    }
  }, [section?._id, allSectionColumns]);

  const checkPermission = useCallback(
    (obj) => {
      const dynamicName = logInRights?.[0]?.rights?.rangePlan?.find((rightEle) => {
        return rightEle?.label === `${obj?.name} (Custom Section)`;
      })?.module;
      return CheckActionPermission(logInRights, 'rangePlan', dynamicName, 'addEdit');
    },
    [logInRights],
  );

  const checkUserPermission = useMemo(() => {
    if (section) {
      return checkPermission(section);
    }
    return false;
  }, [section, checkPermission]);

  /** Mirrors the latest columnTotals slice so autoSave can read it without re-creating itself. */
  const columnTotalsRef = useRef({});

  /** Increments per refresh so a slower, older calculation refresh cannot overwrite a newer one. */
  const calculationRefreshSeqRef = useRef(0);

  /**
   * Re-reads the grid pages holding the just-saved products and copies their server-recomputed
   * Calculation cells into the table, so Calculation columns update without a page reload.
   * Calls the service directly so the Redux section data (and the rest of the grid) is untouched.
   *
   * @param {string[]} devOrderIds - Products whose cells were just saved.
   * @returns {Promise<void>}
   */
  const refreshCalculationColumns = useCallback(
    async (devOrderIds) => {
      const calculationColumnIds = getCalculationColumnIds(allSectionColumns);
      if (!calculationColumnIds.length || !section?._id) return;
      const pages = getPagesForProducts(tableDataRef.current, devOrderIds, pageSize);
      if (!pages.length) return;

      calculationRefreshSeqRef.current += 1;
      const requestSeq = calculationRefreshSeqRef.current;
      try {
        const responses = await Promise.all(
          pages.map((pageIndex) =>
            getCustomSectionDataService({
              sectionId: section._id,
              devOrderId: [],
              timeZone,
              ...filtersRef.current,
              pageIndex,
              pageSize,
              apiName: 'customsection',
              rangeplanId,
            }),
          ),
        );
        if (requestSeq !== calculationRefreshSeqRef.current) return;
        const freshRows = responses.flatMap((response) => (response?.code === 200 && Array.isArray(response?.data?.data) ? response.data.data : []));
        if (!freshRows.length) return;
        setLocalTableData((prevData) => mergeCalculationColumns(prevData, freshRows, calculationColumnIds));
      } catch (error) {
        logError('ProductInfoCustomSections: calculation refresh failed', error);
      }
    },
    [allSectionColumns, section?._id, pageSize, rangeplanId],
  );

  /** Awaits the autosave thunk; on success shows the toast and refreshes Calculation cells, on failure refetches any ticked column totals so the optimistic 'all' delta snaps back to the server's authoritative value. */
  const autoSave = useCallback(
    async (doListingObj) => {
      const ok = await dispatch(action.autoSaveCustomSection(doListingObj));
      if (ok) {
        toasterMsg(t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.UPDATED_SUCCESSFULLY'), 'success');
        refreshCalculationColumns((doListingObj?.devOrderData || []).map((entry) => entry?.devOrderId));
        return;
      }
      const totals = columnTotalsRef.current || {};
      const requests = Object.entries(totals)
        .filter(([, entry]) => Array.isArray(entry?.scopes) && entry.scopes.length > 0)
        .map(([columnId, entry]) => ({ columnId, scopes: [...entry.scopes] }));
      if (requests.length === 0 || !rangeplanId) return;
      dispatch(action.fetchColumnTotals({ rangeplanId, requests, filters: filtersRef.current }));
    },
    [dispatch, t, rangeplanId, refreshCalculationColumns],
  );

  const saveCustomSection = useCallback(
    (devOrderDataArray) => {
      if (!devOrderDataArray || !Array.isArray(devOrderDataArray) || devOrderDataArray.length === 0) {
        return;
      }
      const saveObj = {
        sectionId: section?._id,
        loggedInUserId,
        companyURL: userInfo?.company,
        rangePlanId: rangeplanId,
        devOrderData: devOrderDataArray.map((item) => ({
          devOrderId: item?.devOrderId,
          data: item?.data,
        })),
      };
      autoSave(saveObj);
    },
    [section, loggedInUserId, userInfo?.company, rangeplanId, autoSave],
  );

  useEffect(() => {
    const isDataForCurrentSection = section?._id && customSectionData?.customSectionDetails?._id === section._id;
    const isInitialSync = localTableData.length === 0 || filterResyncPendingRef.current;

    if (
      section?._id &&
      customSectionData?.data &&
      allSectionColumns &&
      isInitialSync &&
      isDataForCurrentSection &&
      currentSectionIdRef.current === section._id
    ) {
      const initializedData = customSectionData?.data?.map((item) => {
        if (item?.expandData && Array.isArray(item.expandData)) {
          item.expandData = item.expandData.map((expItem) => {
            const columns = expItem?.columns || {};

            if (typeof columns === 'object' && !Array.isArray(columns)) {
              const normalizedColumns = { ...columns };

              allSectionColumns?.forEach((colDef) => {
                if (!normalizedColumns[colDef?._id]) {
                  let defaultValue = null;
                  if (colDef?.key === 'Checkbox') {
                    defaultValue = {};
                  } else {
                    defaultValue = null;
                  }

                  normalizedColumns[colDef?._id] = {
                    columnType: 'source',
                    value: defaultValue,
                    columnId: colDef?._id,
                  };
                }
              });

              return {
                ...expItem,
                columns: normalizedColumns,
                datePickerTime: expItem?.datePickerTime || null,
              };
            }

            return {
              ...expItem,
              columns: {},
              datePickerTime: expItem?.datePickerTime || null,
            };
          });
        }
        return {
          ...item,
          datePickerTime: item?.datePickerTime || null,
          isChangesMade: false,
        };
      });

      setLocalTableData(initializedData);
      filterResyncPendingRef.current = false;

      if (customSectionData?.totalCount !== undefined) {
        setHasMore((customSectionData?.data?.length || 0) < customSectionData.totalCount);
      } else {
        setHasMore((customSectionData?.data?.length || 0) >= pageSize);
      }
    }
  }, [
    section?._id,
    customSectionData?.data,
    customSectionData?.customSectionDetails?._id,
    allSectionColumns,
    localTableData.length,
    page,
    customSectionData?.totalCount,
    pageSize,
  ]);

  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMore || !section?._id) return;

    setLoadingMore(true);
    const nextPage = page + 1;
    const minLoaderVisible = new Promise((resolve) => setTimeout(resolve, MIN_LOADER_VISIBLE_MS));
    pendingHasMoreRef.current = hasMore;
    try {
      const retriveObj = {
        sectionId: section?._id,
        devOrderId: [],
        timeZone,
        ...filteredDataObj,
        pageIndex: nextPage,
        pageSize,
        apiName: 'customsection',
        rangeplanId,
      };

      const response = await dispatch(action.getCustomSectionData(retriveObj));

      if (response && response.code === 200) {
        const newData = response.data?.data || [];
        const totalCount = response.data?.totalCount;

        if (newData.length > 0) {
          const initializedNewData = newData.map((item) => {
            if (item?.expandData && Array.isArray(item.expandData)) {
              item.expandData = item.expandData.map((expItem) => {
                const columns = expItem?.columns || {};
                if (typeof columns === 'object' && !Array.isArray(columns)) {
                  const normalizedColumns = { ...columns };
                  allSectionColumns?.forEach((colDef) => {
                    if (!normalizedColumns[colDef?._id]) {
                      let defaultValue = null;
                      if (colDef?.key === 'Checkbox') {
                        defaultValue = {};
                      } else {
                        defaultValue = null;
                      }
                      normalizedColumns[colDef?._id] = {
                        columnType: 'source',
                        value: defaultValue,
                        columnId: colDef?._id,
                      };
                    }
                  });
                  return {
                    ...expItem,
                    columns: normalizedColumns,
                    datePickerTime: expItem?.datePickerTime || null,
                  };
                }
                return {
                  ...expItem,
                  columns: {},
                  datePickerTime: expItem?.datePickerTime || null,
                };
              });
            }
            return {
              ...item,
              datePickerTime: item?.datePickerTime || null,
              isChangesMade: false,
            };
          });

          setLocalTableData((prevData) => {
            const nextData = [...prevData, ...initializedNewData];
            const loadedCount = nextData.length;
            if (totalCount != null && totalCount > 0) {
              pendingHasMoreRef.current = loadedCount < totalCount;
            } else {
              pendingHasMoreRef.current = newData.length === pageSize;
            }
            return nextData;
          });

          setPage(nextPage);
        } else {
          pendingHasMoreRef.current = false;
        }
      } else {
        pendingHasMoreRef.current = false;
      }
    } catch (error) {
      console.error('Error loading more data:', error);
      pendingHasMoreRef.current = false;
    } finally {
      await minLoaderVisible;
      setHasMore(pendingHasMoreRef.current);
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, page, pageSize, section, timeZone, dispatch, allSectionColumns]);

  const expandMoreData = useCallback(
    (index) => {
      setIsExpandAll(false);
      if (expandedRowIndex.includes(index)) {
        setExpandedRowIndex(expandedRowIndex.filter((item) => item !== index));
      } else {
        setExpandedRowIndex([...expandedRowIndex, index]);
      }
    },
    [expandedRowIndex],
  );

  useEffect(() => {
    if (isExpandAll && localTableData?.length > 0) {
      setExpandedRowIndex(localTableData.map((_, index) => index));
    }
  }, [isExpandAll, localTableData?.length]);

  const expandAllProdIdentifier = useCallback(() => {
    const allExpanded = expandedRowIndex?.length > 0 && expandedRowIndex?.length === localTableData?.length;
    if (allExpanded) {
      setExpandedRowIndex([]);
      setIsExpandAll(false);
    } else {
      setExpandedRowIndex(localTableData?.map((_, index) => index) || []);
      setIsExpandAll(true);
    }
  }, [expandedRowIndex?.length, localTableData]);

  const handleLocalSelection = useCallback(
    (itemId) => {
      setLocalSelectedIds((prev) => {
        const newSelectedIds = prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId];

        const totalItems = localTableData.length;
        const selectedItems = newSelectedIds.length;
        setLocalIsSelectAll(selectedItems === totalItems);

        return newSelectedIds;
      });
      handleSelection?.(itemId);
    },
    [localTableData.length, handleSelection],
  );

  const handleLocalSelectAll = useCallback(
    (checked) => {
      setLocalIsSelectAll(checked);
      if (checked) {
        const allIds = localTableData.map((item) => item._id);
        setLocalSelectedIds(allIds);
        handleCustomSectionSelection?.(allIds);
      } else {
        setLocalSelectedIds([]);
        handleCustomSectionSelection?.([]);
      }
    },
    [localTableData, handleSelection],
  );

  const buildChangedCellsPayload = useCallback(
    (cellsMap, tableData) => {
      if (!cellsMap || cellsMap.size === 0) {
        return [];
      }

      const manualColumnIds = uniq(allSectionColumns?.filter((col) => col.columnType === 'manual' || !col.columnType).map((cols) => cols._id) ?? []);
      const devOrderMap = new Map();

      const datePickerTimeMap = new Map();

      cellsMap.forEach((cellData) => {
        const { devOrderId, columnId, combinationId, value, datePickerTime } = cellData;

        if (columnId === 'datePickerTime') {
          const key = `${devOrderId}_${combinationId ?? 'parent'}`;
          if (!datePickerTimeMap.has(key)) {
            datePickerTimeMap.set(key, {
              devOrderId,
              combinationId,
              datePickerTime: value ?? datePickerTime,
            });
          }
          return;
        }

        if (!manualColumnIds.includes(columnId)) {
          return;
        }

        if (!devOrderMap.has(devOrderId)) {
          devOrderMap.set(devOrderId, new Map());
        }

        const combinationMap = devOrderMap.get(devOrderId);
        const comboKey = combinationId !== null && combinationId !== undefined ? combinationId : 'parent';

        if (!combinationMap.has(comboKey)) {
          const item = tableData.find((item) => item._id === devOrderId);
          if (!item) return;

          const datePickerKey = `${devOrderId}_${comboKey}`;
          const datePickerData = datePickerTimeMap.get(datePickerKey);

          let combinationData = {
            columns: [],
            datePickerTime: datePickerData?.datePickerTime ?? datePickerTime ?? null,
          };

          if (combinationId !== null && combinationId !== undefined) {
            const expItem = item.expandData?.[combinationId];
            if (expItem) {
              combinationData = {
                _id: expItem._id,
                size: expItem.sizeName || '',
                length: expItem.lengthId || null,
                colourWay: expItem.colourWayId || null,
                columns: [],
                datePickerTime: datePickerData?.datePickerTime ?? datePickerTime ?? expItem.datePickerTime ?? null,
              };
            }
          } else {
            const firstExpItem = item.expandData?.[0];
            if (firstExpItem) {
              combinationData = {
                _id: firstExpItem._id,
                size: firstExpItem.sizeName || '',
                length: firstExpItem.lengthId || null,
                colourWay: firstExpItem.colourWayId || null,
                columns: [],
                datePickerTime: datePickerData?.datePickerTime ?? datePickerTime ?? firstExpItem.datePickerTime ?? null,
              };
            }
          }

          combinationMap.set(comboKey, combinationData);
        }

        const combinationData = combinationMap.get(comboKey);
        const columnDefinition = allSectionColumns?.find((col) => col?._id === columnId);

        let finalValue = value ?? null;
        if (columnDefinition?.type === 'Status' && !finalValue) {
          const defaultStatus = columnDefinition?.status;
          if (defaultStatus) {
            finalValue = defaultStatus;
          }
        }

        combinationData?.columns?.push({
          columnId,
          value: finalValue,
        });
      });

      const result = [];
      devOrderMap.forEach((combinationMap, devOrderId) => {
        const dataArray = [];
        combinationMap.forEach((combinationData) => {
          dataArray.push(combinationData);
        });

        if (dataArray.length === 0) {
          const datePickerKeys = Array.from(datePickerTimeMap.keys()).filter((key) => key.startsWith(`${devOrderId}_`));
          if (datePickerKeys.length > 0) {
            const item = tableData.find((item) => item?._id === devOrderId);
            if (item && item?.expandData && item.expandData.length > 0) {
              datePickerKeys.forEach((key) => {
                const datePickerData = datePickerTimeMap.get(key);
                const comboId = key.split('_')[1];
                if (comboId === 'parent' && item.expandData?.[0]) {
                  dataArray.push({
                    _id: item.expandData[0]?._id,
                    size: item.expandData[0]?.sizeName || '',
                    length: item.expandData[0]?.lengthId || null,
                    colourWay: item.expandData[0]?.colourWayId || null,
                    columns: [],
                    datePickerTime: datePickerData?.datePickerTime,
                  });
                } else if (item.expandData?.[parseInt(comboId)]) {
                  const expItem = item.expandData[parseInt(comboId)];
                  dataArray.push({
                    _id: expItem?._id,
                    size: expItem?.sizeName || '',
                    length: expItem?.lengthId || null,
                    colourWay: expItem?.colourWayId || null,
                    columns: [],
                    datePickerTime: datePickerData?.datePickerTime,
                  });
                }
              });
            }
          }
        }

        if (dataArray.length > 0) {
          result.push({
            devOrderId,
            data: dataArray,
          });
        }
      });

      return result;
    },
    [allSectionColumns],
  );

  useEffect(() => {
    tableDataRef.current = localTableData;
  }, [localTableData]);

  const debounceFn = useMemo(
    () =>
      debounce(() => {
        setChangedCells((prevChangedCells) => {
          if (prevChangedCells.size === 0) {
            return prevChangedCells;
          }
          pendingSaveRef.current = {
            cells: new Map(prevChangedCells),
            tableData: tableDataRef.current,
          };
          return new Map();
        });
      }, 3000),
    [],
  );

  useEffect(() => {
    if (pendingSaveRef.current && changedCells.size === 0) {
      const { cells, tableData } = pendingSaveRef.current;
      pendingSaveRef.current = null;
      const devOrderDataArray = buildChangedCellsPayload(cells, tableData);
      if (devOrderDataArray.length > 0) {
        saveCustomSection(devOrderDataArray);
      }
    }
  }, [changedCells, buildChangedCellsPayload, saveCustomSection]);

  useEffect(() => {
    return () => {
      if (debounceFn.cancel) {
        debounceFn.cancel();
      }
    };
  }, [debounceFn]);

  /** On first render of a (rangeplan × user × section), restores ticked scopes from localStorage and fires a single batched fetch for everything that's ticked. */
  const restoredKeyRef = useRef(null);
  useEffect(() => {
    if (!rangeplanId || !loggedInUserId) return;
    if (!Array.isArray(allSectionColumns) || allSectionColumns.length === 0) return;
    const key = `${rangeplanId}|${loggedInUserId}|${section?._id || ''}`;
    if (restoredKeyRef.current === key) return;
    restoredKeyRef.current = key;

    const saved = loadColumnScopes(rangeplanId, loggedInUserId);
    const validColumnIds = new Set(allSectionColumns.filter((c) => c?.columnType === 'manual' && c?.type === 'Number').map((c) => String(c._id)));
    const requests = [];
    for (const [columnId, scopes] of Object.entries(saved)) {
      if (!validColumnIds.has(columnId)) continue;
      const ticked = Array.isArray(scopes) ? scopes : [];
      if (ticked.length === 0) continue;
      dispatch(action.toggleColumnTotalScope(columnId, ticked));
      requests.push({ columnId, scopes: ticked });
    }
    if (requests.length > 0) {
      dispatch(action.fetchColumnTotals({ rangeplanId, requests, filters: filtersRef.current }));
    }
  }, [rangeplanId, loggedInUserId, section?._id, allSectionColumns, dispatch]);

  const filtersJson = useMemo(() => JSON.stringify(filteredDataObj || {}), [filteredDataObj]);

  const refetchAllTotalsDebouncedRef = useRef(null);
  if (!refetchAllTotalsDebouncedRef.current) {
    refetchAllTotalsDebouncedRef.current = debounce((rpId, totals, filters) => {
      const requests = Object.entries(totals || {})
        .filter(([, entry]) => Array.isArray(entry?.scopes) && entry.scopes.includes('all'))
        .map(([columnId]) => ({ columnId, scopes: ['all'] }));
      if (requests.length === 0 || !rpId) return;
      dispatch(action.fetchColumnTotals({ rangeplanId: rpId, requests, filters }));
    }, 300);
  }

  useEffect(() => {
    if (lastFiltersJsonRef.current === null) {
      lastFiltersJsonRef.current = filtersJson;
      return;
    }
    if (lastFiltersJsonRef.current === filtersJson) return;
    lastFiltersJsonRef.current = filtersJson;
    filterResyncPendingRef.current = true;
    setPage(1);
    setHasMore(true);
    setLoadingMore(false);
    setExpandedRowIndex([]);
    setIsExpandAll(false);
    setChangedCells(new Map());
    if (!rangeplanId || restoredKeyRef.current === null) return;
    refetchAllTotalsDebouncedRef.current(rangeplanId, columnTotalsRef.current, filteredDataObj || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersJson]);

  /** Keeps columnTotalsRef in sync with the latest columnTotals slice so autoSave's failure handler can read it. */
  const columnTotalsState = useSelector((s) => s.ProductListData?.columnTotals);
  useEffect(() => {
    columnTotalsRef.current = columnTotalsState || {};
  }, [columnTotalsState]);

  const getColumnValueForParent = useCallback(
    (item, columnIndex) => {
      const expandData = item?.expandData || [];

      if (!expandData.length) {
        return '';
      }

      const columnDef = allSectionColumns?.[columnIndex];
      const columnId = columnDef?._id;
      if (!columnId) {
        return '';
      }

      if (expandData.length === 1) {
        const singleValue = expandData[0]?.columns?.[columnId]?.value;
        return singleValue ?? '';
      }

      const values = expandData.map((exp) => {
        const column = exp?.columns?.[columnId];
        return column?.value;
      });

      const firstValue = values[0];

      if (columnDef?.key === 'Checkbox') {
        const allSame = values.every((val) => {
          return JSON.stringify(val || {}) === JSON.stringify(firstValue || {});
        });
        return allSame ? firstValue ?? {} : 'Multiple';
      }

      if (columnDef?.key === 'Date_picker' || columnDef?.key === 'Time_picker') {
        const allSame = values.every((val) => {
          const valStr = val ? String(val) : '';
          const firstStr = firstValue ? String(firstValue) : '';
          return valStr === firstStr;
        });
        return allSame ? firstValue : 'Multiple';
      }

      if (columnDef?.key === 'Status') {
        const allSame = values.every((val) => {
          const valStr = val?.colourName ? String(val?.colourName) : '';
          const firstStr = firstValue?.colourName ? String(firstValue?.colourName) : '';
          return valStr === firstStr;
        });
        return allSame ? firstValue : 'Multiple';
      }

      const allSame = values.every((val) => {
        const isEmpty = (v) => v === null || v === undefined || v === '';

        if (isEmpty(val) && isEmpty(firstValue)) {
          return true;
        }

        if (isEmpty(val) || isEmpty(firstValue)) {
          return false;
        }

        return String(val) === String(firstValue);
      });

      return allSame ? firstValue ?? '' : 'Multiple';
    },
    [allSectionColumns],
  );

  const getDatePickerTimeForParent = useCallback((item) => {
    const expandData = item?.expandData || [];

    if (expandData.length > 0) {
      return expandData[0]?.datePickerTime || null;
    }

    return item?.datePickerTime || null;
  }, []);

  /** Applies a cell edit: computes an optimistic 'all'-scope delta against the pre-edit snapshot, then patches local table state and queues the autosave. */
  const handleChange = useCallback(
    (value, parentIndex, columnIndex, childIndex = null, columnType, isDateTimeToggle) => {
      if (!isDateTimeToggle) {
        const colDefForDelta = allSectionColumns?.[columnIndex];
        if (colDefForDelta && colDefForDelta.columnType === 'manual' && colDefForDelta.type === 'Number') {
          const preItem = tableDataRef.current?.[parentIndex];
          if (preItem) {
            const newNum = Number(value);
            const safeNew = Number.isFinite(newNum) ? newNum : 0;
            const colId = String(colDefForDelta._id);
            const readOld = (combo) => {
              const raw = combo?.columns?.[colDefForDelta._id]?.value;
              const n = Number(raw);
              return Number.isFinite(n) ? n : 0;
            };
            let delta = 0;
            if (childIndex === null) {
              const combos = Array.isArray(preItem.expandData) ? preItem.expandData : [];
              for (const combo of combos) delta += safeNew - readOld(combo);
            } else {
              const combo = preItem?.expandData?.[childIndex];
              delta = safeNew - readOld(combo);
            }
            if (Number.isFinite(delta) && delta !== 0) {
              dispatch(action.applyColumnTotalDelta({ columnId: colId, delta }));
            }
          }
        }
      }

      setLocalTableData((prevData) => {
        if (parentIndex < 0 || parentIndex >= prevData.length) return prevData;
        const prevItem = prevData[parentIndex];
        if (!prevItem) return prevData;

        const updatedData = prevData.slice();
        const item = { ...prevItem };
        const prevExpand = Array.isArray(prevItem.expandData) ? prevItem.expandData : null;
        if (prevExpand) {
          if (childIndex === null) {
            item.expandData = prevExpand.map((c) => ({
              ...c,
              columns: c?.columns && typeof c.columns === 'object' ? { ...c.columns } : c?.columns,
            }));
          } else {
            item.expandData = prevExpand.slice();
            const prevChild = prevExpand[childIndex];
            if (prevChild) {
              item.expandData[childIndex] = {
                ...prevChild,
                columns: prevChild.columns && typeof prevChild.columns === 'object' ? { ...prevChild.columns } : prevChild.columns,
              };
            }
          }
        }
        updatedData[parentIndex] = item;

        const columnDef = allSectionColumns?.[columnIndex];
        const columnId = columnDef?._id;

        if (!columnId) return prevData;

        const isManualColumn = columnDef?.columnType === 'manual' || !columnDef?.columnType;

        if (isDateTimeToggle) {
          if (childIndex === null) {
            item.datePickerTime = value;
            if (item?.expandData && item.expandData.length > 0) {
              item.expandData.forEach((expItem, idx) => {
                expItem.datePickerTime = value;
                const cellKey = `${item?._id}_datePickerTime_${idx}`;
                setChangedCells((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(cellKey, {
                    devOrderId: item?._id,
                    columnId: 'datePickerTime',
                    combinationId: idx,
                    value,
                    datePickerTime: value,
                  });
                  return newMap;
                });
              });
            } else {
              const cellKey = `${item?._id}_datePickerTime_parent`;
              setChangedCells((prev) => {
                const newMap = new Map(prev);
                newMap.set(cellKey, {
                  devOrderId: item?._id,
                  columnId: 'datePickerTime',
                  combinationId: null,
                  value,
                  datePickerTime: value,
                });
                return newMap;
              });
            }
          } else if (item?.expandData && item.expandData?.[childIndex]) {
            item.expandData[childIndex].datePickerTime = value;
            const cellKey = `${item?._id}_datePickerTime_${childIndex}`;
            setChangedCells((prev) => {
              const newMap = new Map(prev);
              newMap.set(cellKey, {
                devOrderId: item?._id,
                columnId: 'datePickerTime',
                combinationId: childIndex,
                value,
                datePickerTime: value,
              });
              return newMap;
            });
          }
        } else if (childIndex === null) {
          if (item?.expandData && item.expandData.length > 0) {
            item.expandData.forEach((expItem, idx) => {
              if (!expItem?.columns || typeof expItem.columns !== 'object') {
                expItem.columns = {};
              }

              if (!expItem.columns[columnId]) {
                const defaultVal = columnType === 'Checkbox' ? {} : null;
                expItem.columns[columnId] = {
                  columnType: 'source',
                  value: defaultVal,
                  columnId,
                };
              }

              let finalValue = value;
              if (columnType === 'Checkbox') {
                const existingValue = expItem.columns?.[columnId]?.value || {};
                finalValue = {
                  ...existingValue,
                  ...value,
                };
                expItem.columns[columnId] = {
                  ...expItem.columns[columnId],
                  value: finalValue,
                };
              } else {
                expItem.columns[columnId] = {
                  ...expItem.columns[columnId],
                  value,
                };
              }

              if (isManualColumn) {
                const cellKey = `${item?._id}_${columnId}_${idx}`;
                setChangedCells((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(cellKey, {
                    devOrderId: item?._id,
                    columnId,
                    combinationId: idx,
                    value: finalValue,
                    datePickerTime: expItem?.datePickerTime ?? null,
                  });
                  return newMap;
                });
              }
            });
          }
        } else if (item?.expandData && item.expandData?.[childIndex]) {
          if (!item.expandData[childIndex]?.columns || typeof item.expandData[childIndex].columns !== 'object') {
            item.expandData[childIndex].columns = {};
          }

          if (!item.expandData[childIndex].columns?.[columnId]) {
            const defaultVal = columnType === 'Checkbox' ? {} : null;
            item.expandData[childIndex].columns[columnId] = {
              columnType: 'manual',
              value: defaultVal,
              columnId,
            };
          }

          let finalValue = value;
          if (columnType === 'Checkbox') {
            const existingValue = item.expandData[childIndex].columns?.[columnId]?.value || {};
            finalValue = {
              ...existingValue,
              ...value,
            };
            item.expandData[childIndex].columns[columnId] = {
              ...item.expandData[childIndex].columns[columnId],
              value: finalValue,
            };
          } else {
            item.expandData[childIndex].columns[columnId] = {
              ...item.expandData[childIndex].columns[columnId],
              value,
            };
          }

          if (isManualColumn) {
            const cellKey = `${item?._id}_${columnId}_${childIndex}`;
            setChangedCells((prev) => {
              const newMap = new Map(prev);
              newMap.set(cellKey, {
                devOrderId: item?._id,
                columnId,
                combinationId: childIndex,
                value: finalValue,
                datePickerTime: item.expandData[childIndex]?.datePickerTime ?? null,
              });
              return newMap;
            });
          }
        }

        debounceFn();

        return updatedData;
      });
    },
    [allSectionColumns, debounceFn, dispatch],
  );

  const createParentOnChange = useCallback(
    (inde, rowInd, row) => {
      return (value, path, isDateTimeToggle) => {
        if (isDateTimeToggle) {
          handleChange(value, inde, rowInd, null, row?.key, isDateTimeToggle);
        } else {
          handleChange(value, inde, rowInd, null, row?.key);
        }
      };
    },
    [handleChange],
  );

  const createChildOnChange = useCallback(
    (inde, rowInd, expInd, row) => {
      return (value, path, isDateTimeToggle) => {
        if (isDateTimeToggle) {
          handleChange(value, inde, rowInd, expInd, row?.key, isDateTimeToggle);
        } else {
          handleChange(value, inde, rowInd, expInd, row?.key);
        }
      };
    },
    [handleChange],
  );

  const imgSrc = (element) => {
    if (!element?.image || isEmpty(element?.image) || element?.image === null) {
      return noImage;
    }
    if (element?.image?.includes('https://') || element?.image?.includes('http://')) {
      return element.image;
    }
    return `${imageUrl}/${element?.image}`;
  };

  const handleProductEdit = (id) => {
    goToProductEdit(id);
  };

  const handlePageNextInScroll = useCallback(() => {
    loadMoreData();
  }, [loadMoreData]);

  const formatCalculatedMeasurement = (value) => {
    if (!value && value !== 0) return value;

    const cleanedValue = value?.toString()?.replace(/&+/g, ' ')?.trim();

    if (cleanedValue?.includes(',') || cleanedValue?.split(/\s+/).length > 1) {
      const parts = cleanedValue.split(/\s+/);
      const allAreDashDash = parts.every((part) => part.trim() === '--');
      if (allAreDashDash) {
        return '--';
      }
      return cleanedValue;
    }

    if (!/^-?\d+(\.\d+)?$/.test(cleanedValue)) {
      return cleanedValue;
    }

    const numValue = parseFloat(cleanedValue);

    const roundedValue = Math.round(numValue * 100) / 100;

    return parseFloat(roundedValue.toFixed(2)).toString();
  };

  const hasErroredForCurrentSection = section?._id && customSectionData?.erroredSectionId === section._id;
  const isWaitingForSectionData =
    section?._id && customSectionData?.customSectionDetails?._id !== section._id && !hasErroredForCurrentSection;
  const isWaitingForTableSync =
    section?._id &&
    customSectionData?.customSectionDetails?._id === section._id &&
    Array.isArray(customSectionData?.data) &&
    customSectionData.data.length > 0 &&
    localTableData.length === 0;

  if ((isWaitingForSectionData || isWaitingForTableSync) && localTableData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-200">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {colouredCardFaces ? (
        <Container fluid>
          <InfiniteScroll
            dataLength={localTableData?.length}
            hasMore={hasMore}
            next={handlePageNextInScroll}
            loader={loadingMore && <Loader />}
            scrollableTarget="ProductInfoRPColouredCardCustomContainer"
            className="fixed-col-container rp-table-container"
          >
            <RenderProductInformationColouredCard
              productsList={localTableData}
              selectedCardAttributes={selectedCardAttributes}
              colouredFaces={colouredFaces}
              handleSelection={handleSelection}
              selectedIds={localSelectedIds}
              handleCombinationSelection={handleCombinationSelection}
              selectedCombinations={selectedCombinations}
            />
          </InfiniteScroll>
        </Container>
      ) : (
        <div
          className={`rp-scroll-container${anyColumnTotalsActive ? ' has-column-totals' : ''}`}
          id="ProductInfoRPTableCustomContainer"
          style={anyColumnTotalsActive ? { '--column-totals-height': `${columnTotalsHeight}px` } : undefined}
        >
          <InfiniteScroll
            dataLength={localTableData?.length}
            hasMore={hasMore}
            next={loadMoreData}
            loader={loadingMore && <Loader />}
            className="fixed-col-container rp-table-container"
            scrollableTarget="ProductInfoRPTableCustomContainer"
          >
            <table id="RPProductInfoCustomSectionsTable" className="table common-custom-table fixed-header mb-0">
              <thead>
                <tr>
                  <th className="w-50px">
                    <div className="common-checkbox margin-auto">
                      <input
                        type="checkbox"
                        onChange={(e) => handleLocalSelectAll(e.target.checked)}
                        name="check_all_custom_section"
                        id="check_all_custom_section"
                        checked={localIsSelectAll}
                        disabled={!localTableData?.length}
                      />
                      <label htmlFor="check_all_custom_section" className="chklabel" />
                    </div>
                  </th>

                  <th className="w-50px">
                    <button type="button" className="btn-up-down" aria-label={t('COMMON_LABEL.COLLAPSE')} onClick={() => expandAllProdIdentifier()}>
                      <span
                        className={`icon-new-down-arrow-icon ${
                          isExpandAll || (localTableData?.length > 0 && localTableData?.length === expandedRowIndex?.length) ? 'icon-inverse' : ''
                        }`}
                      />
                    </button>
                  </th>

                  <th className="w-80px">{t('RANGE_PLANS.PRODUCT_LIST.IMAGE')}</th>

                  <th className="w-220px first-fix-col">{t('RANGE_PLANS.PRODUCT_LIST.PRODUCT_NUMBER')}</th>

                  {customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                    <>
                      <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.COLOURWAY')}</th>
                      <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.SIZE')}</th>
                      <th className="w-224px ">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.LENGTH')}</th>
                    </>
                  ) : customSectionDetails?.colourway && !customSectionDetails?.sizeLength ? (
                    <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.COLOURWAY')}</th>
                  ) : !customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                    <>
                      <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.SIZE')}</th>
                      <th className="w-224px">{t('RANGE_PLANS.PRODUCT_LIST.EXPAND_VIEW_MODULES.PRODUCT_INFO_CUSTOM_SECTION.LENGTH')}</th>
                    </>
                  ) : null}

                  {checkArray(allSectionColumns) &&
                    allSectionColumns.length > 0 &&
                    allSectionColumns?.map((col, ind) => {
                      return (
                        <th className="rp-custom-section-th" key={ind}>
                          <div className="rp-custom-section-th__inner">
                            <ColumnTotalsDisplay columnId={String(col._id)} subType={col.subType} clientTotals={clientSideTotals[String(col._id)]} />
                            <div className="rp-custom-section-th__header">
                              <span>{col.title}</span>
                              <ColumnCalculation column={col} rangeplanId={rangeplanId} userId={loggedInUserId} filters={filteredDataObj} />
                            </div>
                          </div>
                        </th>
                      );
                    })}
                </tr>
              </thead>
              <tbody>
                {checkArray(localTableData) ? (
                  localTableData?.map((item, inde) => {
                    const productImage = imgSrc(item);
                    const customCombination = item?.expandData || [];

                    return (
                      <>
                        <tr key={`parent_${inde}`}>
                          <td>
                            <div className="common-checkbox margin-auto">
                              <input
                                onChange={() => {
                                  handleLocalSelection(item?._id);
                                }}
                                name={`checks_${inde}`}
                                id={`checks_${inde}`}
                                type="checkbox"
                                checked={localSelectedIds?.filter((val) => val === item?._id)?.length > 0}
                              />
                              <label htmlFor={`checks_${inde}`} className="chklabel" />
                            </div>
                          </td>
                          <td>
                            {customCombination?.length > 1 ? (
                              <button type="button" className="btn-up-down" aria-label={t('COMMON_LABEL.COLLAPSE')} onClick={() => expandMoreData(inde)}>
                                {expandedRowIndex.includes(inde) ? (
                                  <span className="icon-new-down-arrow-icon icon-inverse" />
                                ) : (
                                  <span className="icon-new-down-arrow-icon" />
                                )}
                              </button>
                            ) : null}
                          </td>
                          <td>
                            <CommonImageComponent productImage={productImage} />
                          </td>

                          <td className="first-fix-col">
                            <div className="d-flex align-items-center">
                              {shortenandHoverString(item?.number || '--', 30)}
                              {rangePlanAddEditAccess && (
                                <button type="button" className="btn ml-auto" aria-label="Edit" onClick={() => handleProductEdit(item?._id)}>
                                  <i className="icon-Edit-1 f-22" />
                                </button>
                              )}
                              <Link
                                target="_blank"
                                to={`/development-order/${item?._id}/design-details/list`}
                                className={!rangePlanAddEditAccess ? 'text-decoration-none ml-auto' : 'text-decoration-none'}
                              >
                                <span className="icon-redirect f-20" />
                              </Link>
                            </div>
                          </td>
                          {customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                            <>
                              <td>
                                <p className="w-150px mb-0">{shortenandHoverString(item?.colourLibraries?.map((col) => col?.name)?.join(', ') || '--', 20)}</p>
                              </td>
                              <td>
                                <p className="w-150px mb-0">{item?.sizeRanges?.join(', ') || '--'}</p>
                              </td>
                              <td>
                                <p className="w-150px mb-0">{item?.legLengths?.map((len) => len?.name)?.join(', ') || '--'}</p>
                              </td>{' '}
                            </>
                          ) : customSectionDetails?.colourway && !customSectionDetails?.sizeLength ? (
                            <td>
                              <p className="w-150px mb-0">{shortenandHoverString(item?.colourLibraries?.map((col) => col?.name)?.join(', ') || '--', 20)}</p>
                            </td>
                          ) : !customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                            <>
                              <td>
                                <p className="w-150px mb-0">{item?.sizeRanges?.join(', ') || '--'}</p>
                              </td>
                              <td>
                                <p className="w-150px mb-0 ">{item?.legLengths?.map((len) => len?.name)?.join(', ') || '--'}</p>
                              </td>
                            </>
                          ) : null}

                          {checkArray(allSectionColumns)
                            ? allSectionColumns?.map((row, rowInd) => {
                                let cellContent;
                                if (row?.columnType === 'manual' || !row?.columnType) {
                                  const parentValue = getColumnValueForParent(item, rowInd);
                                  const parentDatePickerTime = getDatePickerTimeForParent(item);
                                  if (parentValue === 'Multiple') {
                                    cellContent = <td key={row._id}>Multiple</td>;
                                  } else {
                                    const safeValue = parentValue;
                                    cellContent = (
                                      <td key={row._id}>
                                        <RenderHtmlComponent
                                          {...row}
                                          value={safeValue}
                                          keyType={row?.key}
                                          index={rowInd}
                                          pIndex={rowInd}
                                          inde={inde}
                                          childIndex={null}
                                          devOrderId={item?._id}
                                          customSectionId={customSectionDetails?._id}
                                          datePickerTime={parentDatePickerTime}
                                          onChange={createParentOnChange(inde, rowInd, row)}
                                          disableCondition={!checkUserPermission}
                                          statusColoursOptions={row?.statusColoursOptions || []}
                                        />
                                      </td>
                                    );
                                  }
                                } else if (row?.columnType === 'source') {
                                  const firstChildColumns = item?.expandData?.[0]?.columns;
                                  const columnDetails = firstChildColumns?.[row?._id]?.columnDetails;
                                  const cellValue = !columnDetails?.isSameValue && item?.expandData.length > 1 ? '--' : firstChildColumns?.[row?._id]?.value;
                                  const formattedValue = ['originalSubmission', 'originalDownload', 'mostRecentSubmission', 'mostRecentDownload'].includes(
                                    row?.source?.subLocation?.name,
                                  )
                                    ? '--'
                                    : formatCellValue(cellValue);
                                  cellContent = (
                                    <td key={row._id}>
                                      <p className="w-150px mb-0">{formattedValue !== '--' ? shortenandHoverString(formattedValue, 20) : '--'}</p>
                                    </td>
                                  );
                                } else if (row?.columnType === 'calculation') {
                                  const firstChildColumns = item?.expandData?.[0]?.columns;
                                  const columnDetails = firstChildColumns?.[row?._id]?.columnDetails;
                                  const cellValue = !columnDetails?.isSameValue && item?.expandData.length > 1 ? '--' : firstChildColumns?.[row?._id]?.value;
                                  return <td key={row._id}>{formatCalculatedMeasurement(cellValue) || '--'}</td>;
                                } else {
                                  cellContent = <td key={row._id}>--</td>;
                                }
                                return cellContent;
                              })
                            : ''}
                        </tr>
                        {expandedRowIndex.includes(inde) &&
                          customCombination?.map((expItems, expInd) => (
                            <tr key={`child_${inde}_${expInd}`}>
                              <td />
                              <td />
                              <td />
                              <td className="first-fix-col" />
                              {customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                                <>
                                  <td>
                                    <p className="w-150px mb-0">{expItems?.colourWayName || '--'}</p>
                                  </td>
                                  <td>
                                    <p className="w-150px mb-0">{expItems?.sizeName || '--'}</p>
                                  </td>
                                  <td>
                                    <p className="w-150px mb-0 ">{expItems?.lengthName || '--'}</p>
                                  </td>
                                </>
                              ) : customSectionDetails?.colourway && !customSectionDetails?.sizeLength ? (
                                <td>
                                  <p className="w-150px mb-0 ">{expItems?.colourWayName || '--'}</p>
                                </td>
                              ) : !customSectionDetails?.colourway && customSectionDetails?.sizeLength ? (
                                <>
                                  <td>
                                    <p className="w-150px mb-0">{expItems?.sizeName || '--'}</p>
                                  </td>
                                  <td>
                                    <p className="w-150px mb-0 ">{expItems?.lengthName || '--'}</p>
                                  </td>
                                </>
                              ) : null}
                              {checkArray(allSectionColumns)
                                ? allSectionColumns?.map((row, rowInd) => {
                                    let cellContent;
                                    if (row?.columnType === 'manual' || !row?.columnType) {
                                      const childValue = expItems?.columns?.[row._id]?.value ?? '';
                                      const childDatePickerTime = expItems?.datePickerTime || null;
                                      cellContent = (
                                        <td className={row?.key === 'Status' ? 'p-0 w-150px position-relative' : undefined} key={row._id}>
                                          <RenderHtmlComponent
                                            {...row}
                                            value={childValue}
                                            keyType={row?.key}
                                            index={rowInd}
                                            pIndex={rowInd}
                                            inde={inde}
                                            childIndex={expInd}
                                            devOrderId={item?._id}
                                            customSectionId={customSectionDetails?._id}
                                            datePickerTime={childDatePickerTime}
                                            onChange={createChildOnChange(inde, rowInd, expInd, row)}
                                            disableCondition={!checkUserPermission}
                                          />
                                        </td>
                                      );
                                    } else if (row?.columnType === 'calculation') {
                                      const cellValue = expItems?.columns?.[row?._id]?.value;
                                      const columnDetails = expItems?.columns?.[row?._id]?.columnDetails;
                                      const formattedValue = formatCalculatedMeasurement(cellValue) || '--';
                                      cellContent = (
                                        <td key={row._id}>
                                          {columnDetails?.isSameValue ? (
                                            <p className="w-150px mb-0">--</p>
                                          ) : (
                                            <p className="w-150px mb-0">{formattedValue !== '--' ? shortenandHoverString(formattedValue, 60) : '--'}</p>
                                          )}
                                        </td>
                                      );
                                    } else if (row?.columnType === 'source') {
                                      const cellValue = expItems?.columns?.[row?._id]?.value;
                                      const columnDetails = expItems?.columns?.[row?._id]?.columnDetails;
                                      const formattedValue = formatCellValue(cellValue);
                                      cellContent = (
                                        <td key={row._id}>
                                          {columnDetails?.isSameValue ? (
                                            <p className="w-150px mb-0">--</p>
                                          ) : (
                                            <p className="w-150px mb-0">{formattedValue !== '--' ? shortenandHoverString(formattedValue, 60) : '--'}</p>
                                          )}
                                        </td>
                                      );
                                    } else {
                                      cellContent = <td key={row._id}>--</td>;
                                    }

                                    return cellContent;
                                  })
                                : ''}
                            </tr>
                          ))}
                      </>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={getCustomSectionColSpan(customSectionDetails, allSectionColumns)} className="text-center">
                      {t('RANGE_PLANS.PRODUCT_LIST.NO_RECORDS_FOUND')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}

export default ProductInfoCustomSections;
